import { useCallback, useEffect, useRef, useState } from 'react';

import { StorageApi } from '@/api';
import { FeatureFlagKeys, isFeatureEnabled } from '@/config';

import { AudioProcessor } from '../audioProcessing';
import { ADVANCED_AUDIO_CONFIG } from '../config/VoiceToText.config';
import { VoiceToTextProps } from '../types';

const LIVE_TRANSCRIPTION_ENDPOINT =
  'https://api.openai.com/v1/realtime?intent=transcription';
const LIVE_TEXT_FLUSH_DELAY_MS = 120;
const LIVE_STOP_DRAIN_TIMEOUT_MS = 1500;
const LIVE_BUFFER_TOO_SMALL_ERROR = 'buffer too small';
const MIC_SUPPORT_ERROR =
  'Microphone APIs are unavailable in this runtime. Ensure HTTPS/localhost (or Electron media permissions) and try again.';
const LIVE_TRANSCRIPTION_SESSION_UPDATE = {
  type: 'transcription_session.update',
  session: {
    input_audio_format: 'pcm16',
    input_audio_transcription: {
      model: 'gpt-4o-transcribe',
      prompt: '',
      language: 'en',
    },
    turn_detection: {
      type: 'server_vad',
      threshold: 0.5,
      prefix_padding_ms: 300,
      silence_duration_ms: 500,
    },
    input_audio_noise_reduction: {
      type: 'near_field',
    },
    include: ['item.input_audio_transcription.logprobs'],
  },
} as const;
export const useVoiceToText = ({
  handleTextOutput,
  handleError,
}: VoiceToTextProps) => {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const isStoppingRef = useRef(false);
  const audioProcessorRef = useRef<AudioProcessor | null>(null);
  const livePeerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const liveDataChannelRef = useRef<RTCDataChannel | null>(null);
  const liveTranscriptByItemRef = useRef<Record<string, string>>({});
  const liveEmittedLengthByItemRef = useRef<Record<string, number>>({});
  const livePendingIncrementRef = useRef('');
  const liveFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveStopInProgressRef = useRef(false);
  const liveStopDrainResolverRef = useRef<(() => void) | null>(null);
  const handleErrorRef = useRef(handleError);
  const handleTextOutputRef = useRef(handleTextOutput);
  handleErrorRef.current = handleError;
  handleTextOutputRef.current = handleTextOutput;

  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState('');
  const [recordingSizeMB, setRecordingSizeMB] = useState(0);

  const reportError = useCallback((msg: string) => {
    setError(msg);
    handleErrorRef.current?.(msg);
  }, []);

  const flushLivePendingIncrement = useCallback(() => {
    if (!livePendingIncrementRef.current) {
      return;
    }
    handleTextOutputRef.current(livePendingIncrementRef.current);
    livePendingIncrementRef.current = '';
  }, []);

  const queueLiveIncrement = useCallback(
    (text: string) => {
      if (!text) {
        return;
      }

      livePendingIncrementRef.current += text;
      if (liveFlushTimerRef.current) {
        return;
      }

      liveFlushTimerRef.current = setTimeout(() => {
        flushLivePendingIncrement();
        liveFlushTimerRef.current = null;
      }, LIVE_TEXT_FLUSH_DELAY_MS);
    },
    [flushLivePendingIncrement],
  );

  const clearLiveRealtimeBuffers = useCallback(() => {
    liveTranscriptByItemRef.current = {};
    liveEmittedLengthByItemRef.current = {};
    livePendingIncrementRef.current = '';
    if (liveFlushTimerRef.current) {
      clearTimeout(liveFlushTimerRef.current);
      liveFlushTimerRef.current = null;
    }
  }, []);

  const resolveLiveStopDrain = useCallback(() => {
    if (!liveStopDrainResolverRef.current) {
      return;
    }

    const resolve = liveStopDrainResolverRef.current;
    liveStopDrainResolverRef.current = null;
    resolve();
  }, []);

  const stopStreamTracksOnly = useCallback(() => {
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch (err) {
            // Track stop failed
          }
        });
      } catch (err) {
        // Stream tracks cleanup failed
      }
      streamRef.current = null;
    }
  }, []);

  const stopAllTracks = useCallback(() => {
    resolveLiveStopDrain();
    liveStopInProgressRef.current = false;
    clearLiveRealtimeBuffers();

    if (liveDataChannelRef.current) {
      try {
        liveDataChannelRef.current.close();
      } catch (err) {
        // Realtime data channel close failed
      }
      liveDataChannelRef.current = null;
    }

    if (livePeerConnectionRef.current) {
      try {
        livePeerConnectionRef.current.close();
      } catch (err) {
        // Realtime peer connection close failed
      }
      livePeerConnectionRef.current = null;
    }

    // Stop audio processor first
    if (audioProcessorRef.current) {
      try {
        audioProcessorRef.current.cleanup();
      } catch (err) {
        // Audio processor cleanup failed
      }
      audioProcessorRef.current = null;
    }

    // Stop media stream tracks aggressively
    stopStreamTracksOnly();

    // Clear MediaRecorder reference
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current = null;
    }
  }, [clearLiveRealtimeBuffers, resolveLiveStopDrain, stopStreamTracksOnly]);

  const waitForLiveStopDrain = useCallback((timeoutMs: number) => {
    return new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) {
          return;
        }
        settled = true;
        if (liveStopDrainResolverRef.current === finish) {
          liveStopDrainResolverRef.current = null;
        }
        resolve();
      };

      liveStopDrainResolverRef.current = finish;
      setTimeout(finish, timeoutMs);
    });
  }, []);

  const generateAudioTranscript = async (blob: Blob) => {
    if (!blob || blob.size === 0) {
      return '';
    }

    // ALWAYS validate audio before sending to API - do not proceed without AudioProcessor
    if (!audioProcessorRef.current) {
      return 'NO_SPEECH_DETECTED';
    }

    const hasSufficientAudio = audioProcessorRef.current.hasSufficientAudio();

    // Get analysis report regardless of hasSufficientAudio result
    const report = audioProcessorRef.current.getAnalysisReport();

    // STRICT VALIDATION - Block API call if audio doesn't meet requirements
    if (!hasSufficientAudio) {
      if (report.isSilence) {
        return 'SILENCE_DETECTED';
      } else if (report.isMusicOnly) {
        return 'MUSIC_ONLY_DETECTED';
      } else {
        return 'NO_SPEECH_DETECTED';
      }
    }

    // Additional validation even if hasSufficientAudio returns true
    if (report.isSilence) {
      return 'SILENCE_DETECTED';
    }

    if (report.isMusicOnly) {
      return 'MUSIC_ONLY_DETECTED';
    }

    if (!report.hasSpeech) {
      return 'NO_SPEECH_DETECTED';
    }

    try {
      setIsTranscribing(true);
      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');

      const response = await StorageApi.transcribeAudio(formData);
      const data = await response?.data;
      const transcript = data?.transcript || '';

      // Handle backend's "no speech detected" response
      if (
        transcript.toLowerCase().includes('no speech detected') ||
        transcript.trim() === ''
      ) {
        return 'NO_SPEECH_DETECTED';
      }

      return transcript;
    } catch (err: any) {
      return `TRANSCRIPTION_ERROR: ${err?.message}`;
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleLiveTranscriptionEvent = useCallback(
    (payload: string) => {
      try {
        const event = JSON.parse(payload);
        const eventType = event?.type as string | undefined;
        const itemId =
          event?.item_id || event?.item?.id || event?.conversation_item_id;
        const transcriptKey = itemId || 'global';

        const isDeltaEvent =
          eventType === 'conversation.item.input_audio_transcription.delta' ||
          eventType === 'input_audio_transcription.delta' ||
          eventType === 'transcript.delta';
        const isSegmentEvent =
          eventType === 'conversation.item.input_audio_transcription.segment' ||
          eventType === 'input_audio_transcription.segment' ||
          eventType === 'transcript.segment';
        const isCompletedEvent =
          eventType ===
            'conversation.item.input_audio_transcription.completed' ||
          eventType === 'input_audio_transcription.completed' ||
          eventType === 'transcript.completed';

        if (isDeltaEvent) {
          const deltaText =
            event?.delta || event?.transcript || event?.text || '';
          if (deltaText) {
            const previousText =
              liveTranscriptByItemRef.current[transcriptKey] || '';
            const nextText = previousText + deltaText;
            const emittedLength =
              liveEmittedLengthByItemRef.current[transcriptKey] || 0;
            const increment = nextText.slice(emittedLength);

            queueLiveIncrement(increment);
            liveTranscriptByItemRef.current[transcriptKey] = nextText;
            liveEmittedLengthByItemRef.current[transcriptKey] = nextText.length;
          }
        }

        if (isSegmentEvent) {
          const segmentText = event?.text || event?.transcript || '';
          if (segmentText) {
            const previousText =
              liveTranscriptByItemRef.current[transcriptKey] || '';
            const shouldAppendSpace =
              previousText.length > 0 &&
              !previousText.endsWith(' ') &&
              !segmentText.startsWith(' ');
            const normalizedSegment = shouldAppendSpace
              ? ` ${segmentText}`
              : segmentText;
            const nextText = previousText + normalizedSegment;
            const emittedLength =
              liveEmittedLengthByItemRef.current[transcriptKey] || 0;
            const increment = nextText.slice(emittedLength);

            queueLiveIncrement(increment);
            liveTranscriptByItemRef.current[transcriptKey] = nextText;
            liveEmittedLengthByItemRef.current[transcriptKey] = nextText.length;
          }
        }

        if (isCompletedEvent) {
          const completedText = event?.transcript || event?.text || '';
          const knownText =
            liveTranscriptByItemRef.current[transcriptKey] || '';
          const finalText =
            completedText.length >= knownText.length
              ? completedText
              : knownText;
          const emittedLength =
            liveEmittedLengthByItemRef.current[transcriptKey] || 0;
          const increment = finalText.slice(emittedLength);

          queueLiveIncrement(increment);

          delete liveTranscriptByItemRef.current[transcriptKey];
          delete liveEmittedLengthByItemRef.current[transcriptKey];
          if (livePendingIncrementRef.current) {
            flushLivePendingIncrement();
          }

          if (liveStopInProgressRef.current) {
            resolveLiveStopDrain();
          }
        }

        if (eventType === 'error' && event?.error?.message) {
          const errorMessage = String(event.error.message).toLowerCase();
          if (
            liveStopInProgressRef.current &&
            errorMessage.includes(LIVE_BUFFER_TOO_SMALL_ERROR)
          ) {
            resolveLiveStopDrain();
            return;
          }
          reportError(`Live transcription error: ${event.error.message}`);
        }

        if (
          eventType === 'conversation.item.input_audio_transcription.failed' &&
          event?.error?.message
        ) {
          reportError(`Live transcription failed: ${event.error.message}`);
        }

        if (eventType === 'transcription_session.updated') {
          // Session update accepted by server.
        }
      } catch (err) {
        // Ignore invalid realtime payloads
      }
    },
    [
      flushLivePendingIncrement,
      queueLiveIncrement,
      reportError,
      resolveLiveStopDrain,
    ],
  );

  const startLiveRecording = useCallback(async (): Promise<boolean> => {
    setError('');
    setIsTranscribing(true);
    clearLiveRealtimeBuffers();

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(MIC_SUPPORT_ERROR);
      }

      stopAllTracks();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: ADVANCED_AUDIO_CONFIG.constraints as MediaTrackConstraints,
      });

      streamRef.current = stream;

      const sessionResponse = await StorageApi.createLiveTranscriptionSession();
      const clientSecret = sessionResponse?.data?.clientSecret;
      if (!clientSecret) {
        throw new Error('Missing live transcription session token.');
      }

      const peerConnection = new RTCPeerConnection();
      livePeerConnectionRef.current = peerConnection;

      stream.getTracks().forEach((track) => {
        peerConnection.addTrack(track, stream);
      });

      const dataChannel = peerConnection.createDataChannel('oai-events');
      liveDataChannelRef.current = dataChannel;

      dataChannel.onopen = () => {
        try {
          dataChannel.send(JSON.stringify(LIVE_TRANSCRIPTION_SESSION_UPDATE));
        } catch (err: any) {
          reportError(
            `Failed to configure live transcription session: ${
              err?.message || 'Unknown error'
            }.`,
          );
        }
      };
      dataChannel.onmessage = (event) => {
        handleLiveTranscriptionEvent(event.data);
      };
      dataChannel.onerror = () => {
        reportError('Live transcription connection encountered an error.');
      };
      dataChannel.onclose = () => {
        liveDataChannelRef.current = null;
        resolveLiveStopDrain();
      };

      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);

      const sdpResponse = await fetch(LIVE_TRANSCRIPTION_ENDPOINT, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${clientSecret}`,
          'Content-Type': 'application/sdp',
        },
      });

      if (!sdpResponse.ok) {
        const errorText = await sdpResponse.text();
        throw new Error(
          errorText || 'Failed to initialize live transcription.',
        );
      }

      const answerSdp = await sdpResponse.text();
      await peerConnection.setRemoteDescription({
        type: 'answer',
        sdp: answerSdp,
      });

      setIsRecording(true);
      return true;
    } catch (err: any) {
      stopAllTracks();
      reportError(
        `Failed to start live transcription: ${
          err?.message || 'Unknown error'
        }.`,
      );
      setIsRecording(false);
      return false;
    } finally {
      setIsTranscribing(false);
    }
  }, [
    clearLiveRealtimeBuffers,
    handleLiveTranscriptionEvent,
    reportError,
    resolveLiveStopDrain,
    stopAllTracks,
  ]);

  const startBatchRecording = useCallback(async () => {
    // Prevent multiple start calls
    if (isRecording || isStoppingRef.current) {
      return;
    }

    // Reset stopping flag and clear any previous errors
    isStoppingRef.current = false;
    setError('');

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(MIC_SUPPORT_ERROR);
      }

      // Clean up any existing resources first
      stopAllTracks();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: ADVANCED_AUDIO_CONFIG.constraints as MediaTrackConstraints,
      });

      streamRef.current = stream;

      // Initialize audio processor
      audioProcessorRef.current = new AudioProcessor();

      // Check MediaRecorder support
      const supportedMimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
      ];

      let mimeType = '';
      for (const type of supportedMimeTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType || undefined,
      });
      const chunks: Blob[] = [];

      mediaRecorder.onstart = () => {
        setIsRecording(true);
        setRecordingSizeMB(0);
        setError('');

        // Setup audio monitoring
        if (audioProcessorRef.current) {
          audioProcessorRef.current.setupMonitoring(stream);
        }
      };

      mediaRecorder.onerror = (event: any) => {
        // Always clean up on any error to release microphone
        let errorMessage = '';
        if (event.error === 'not-allowed') {
          errorMessage =
            'Microphone access denied. Please allow microphone access and try again.';
        } else if (event.error === 'audio-capture') {
          errorMessage =
            'Audio capture failed. Please check your microphone connection.';
        } else if (event.error === 'network') {
          errorMessage =
            'Network error occurred. Please check your internet connection.';
        } else {
          errorMessage = `Recording error: ${event.error}. Please try again.`;
        }

        reportError(errorMessage);
        setIsRecording(false);
        stopAllTracks();
      };

      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunks.push(e.data);

          const totalBytes = chunks.reduce((acc, chunk) => acc + chunk.size, 0);
          const sizeInMB = totalBytes / (1024 * 1024);
          setRecordingSizeMB(sizeInMB);

          // Use the new config limit
          if (sizeInMB >= ADVANCED_AUDIO_CONFIG.limits.maxSizeMB) {
            reportError(
              `Recording size limit reached (${ADVANCED_AUDIO_CONFIG.limits.maxSizeMB}MB), stopping recording`,
            );
            if (mediaRecorder.state === 'recording') {
              mediaRecorder.stop();
            }
          }
        }
      };

      mediaRecorder.onstop = async () => {
        // Prevent multiple stop handlers from running
        if (isStoppingRef.current) {
          return;
        }
        isStoppingRef.current = true;

        setIsRecording(false);
        setRecordingSizeMB(0);

        if (chunks.length > 0) {
          const blob = new Blob(chunks, {
            type: mediaRecorder.mimeType || 'audio/webm',
          });

          // Check minimum size (1KB)
          const minSizeBytes = 1024;
          if (blob.size >= minSizeBytes) {
            try {
              const result = await generateAudioTranscript(blob);

              if (result === 'SILENCE_DETECTED') {
                reportError(
                  'No speech detected - only silence recorded. Please speak into the microphone.',
                );
              } else if (result === 'MUSIC_ONLY_DETECTED') {
                reportError(
                  'Louder background music/noise detected. Please speak more audibly.',
                );
              } else if (result === 'NO_SPEECH_DETECTED') {
                reportError(
                  'No speech detected. Please speak clearly into the microphone.',
                );
              } else if (result.startsWith('TRANSCRIPTION_ERROR:')) {
                reportError(
                  result.replace(
                    'TRANSCRIPTION_ERROR: ',
                    'Failed to process audio: ',
                  ),
                );
              } else if (result.trim()) {
                handleTextOutputRef.current(result);
              } else {
                reportError(
                  'No speech detected in the recording. Please try speaking more clearly.',
                );
              }
            } catch (err) {
              reportError('Failed to process audio. Please try again.');
            }
          } else {
            reportError(
              `Recording too short (${Math.round(
                blob.size / 1024,
              )}KB). Please record for at least a few seconds.`,
            );
          }
        } else {
          reportError(
            'No audio data recorded. Please check your microphone and try again.',
          );
        }

        // Clean up AudioProcessor after analysis is complete
        if (audioProcessorRef.current) {
          try {
            audioProcessorRef.current.cleanup();
          } catch (err) {
            // AudioProcessor cleanup failed
          }
          audioProcessorRef.current = null;
        }

        isStoppingRef.current = false;
      };

      // Start recording with regular data events for better monitoring
      mediaRecorder.start(1000); // Get data every 1 second
      mediaRecorderRef.current = mediaRecorder;
    } catch (err: any) {
      let errorMessage = '';
      if (
        err.name === 'NotAllowedError' ||
        err.name === 'PermissionDeniedError'
      ) {
        errorMessage =
          'Microphone access denied. Please allow microphone access and try again.';
      } else if (err.name === 'NotFoundError') {
        errorMessage =
          'No microphone found. Please connect a microphone and try again.';
      } else if (err.name === 'NotReadableError') {
        errorMessage =
          'Microphone is being used by another application. Please close other apps using the microphone and try again.';
      } else {
        errorMessage = `Failed to start recording: ${
          err.message || 'Unknown error'
        }. Please check your microphone settings.`;
      }

      reportError(errorMessage);
      setIsRecording(false);
      stopAllTracks();
    }
  }, [isRecording, reportError, stopAllTracks]);

  const startRecording = useCallback(() => {
    const hasMicApi =
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function';

    const canUseLiveRealtime =
      isFeatureEnabled(FeatureFlagKeys.LIVE_STT_REALTIME) &&
      typeof window !== 'undefined' &&
      'RTCPeerConnection' in window &&
      hasMicApi;

    if (canUseLiveRealtime) {
      void (async () => {
        const isLiveStarted = await startLiveRecording();
        if (!isLiveStarted) {
          void startBatchRecording();
        }
      })();
      return;
    }

    void startBatchRecording();
  }, [startBatchRecording, startLiveRecording]);

  const stopRecording = useCallback(() => {
    setIsRecording(false);
    setIsTranscribing(false);

    if (livePeerConnectionRef.current || liveDataChannelRef.current) {
      if (liveStopInProgressRef.current) {
        return;
      }

      liveStopInProgressRef.current = true;
      setIsTranscribing(true);
      flushLivePendingIncrement();
      stopStreamTracksOnly();

      const dataChannel = liveDataChannelRef.current;
      if (dataChannel?.readyState === 'open') {
        try {
          dataChannel.send(
            JSON.stringify({ type: 'input_audio_buffer.commit' }),
          );
        } catch (err) {
          // Commit request failed, timeout-based drain will still run.
        }
      }

      void (async () => {
        await waitForLiveStopDrain(LIVE_STOP_DRAIN_TIMEOUT_MS);
        flushLivePendingIncrement();
        setIsTranscribing(false);
        stopAllTracks();
      })();
      return;
    }

    // Stop the MediaRecorder first, but DON'T clean up AudioProcessor yet
    // The AudioProcessor is needed in mediaRecorder.onstop for audio analysis
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      try {
        mediaRecorderRef.current.stop();
      } catch (err) {
        // MediaRecorder stop failed, but tracks are already stopped
      }
    }

    // Stop media stream tracks but preserve AudioProcessor for analysis
    stopStreamTracksOnly();
  }, [
    flushLivePendingIncrement,
    stopAllTracks,
    stopStreamTracksOnly,
    waitForLiveStopDrain,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllTracks();
    };
  }, [stopAllTracks]);

  return {
    isTranscribing,
    isRecording,
    startRecording,
    stopRecording,
    error,
    recordingSizeMB,
  };
};
